import { Suspense, useEffect, useRef, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { useGLTF } from "@react-three/drei/core/Gltf";
import { easing } from "maath";
import * as THREE from "three";

const MODEL_PATH = "/models/hand-phone.glb";

function useWindowPointer() {
  const pointer = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const onMove = (event) => {
      const container = document.querySelector(".hero-hand-3d");

      if (container) {
        const rect = container.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        if (event.clientX > centerX) {
          pointer.current.x =
            (event.clientX - centerX) / (window.innerWidth - centerX);
        } else {
          pointer.current.x = (event.clientX - centerX) / centerX;
        }

        if (event.clientY > centerY) {
          pointer.current.y =
            -(event.clientY - centerY) / (window.innerHeight - centerY);
        } else {
          pointer.current.y = -(event.clientY - centerY) / centerY;
        }
      } else {
        pointer.current.x = (event.clientX / window.innerWidth) * 2 - 1;
        pointer.current.y = -(event.clientY / window.innerHeight) * 2 + 1;
      }
    };

    const onTouch = (e) => {
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        const container = document.querySelector(".hero-hand-3d");
        if (container) {
          const rect = container.getBoundingClientRect();
          const centerX = rect.left + rect.width / 2;
          const centerY = rect.top + rect.height / 2;
          pointer.current.x = ((touch.clientX - centerX) / centerX) * 1.5;
          pointer.current.y = (-(touch.clientY - centerY) / centerY) * 1.5;
        }
      }
    };

    const onTouchEnd = () => {
      pointer.current.x = 0;
      pointer.current.y = 0;
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("touchmove", onTouch, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onTouch);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, []);

  return pointer;
}

function createScreenOnlyEmissiveMap(originalMap) {
  const image = originalMap.image;
  const canvas = document.createElement("canvas");
  canvas.width = image.width;
  canvas.height = image.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, 0, 0);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  for (let i = 0; i < data.length; i += 4) {
    const brightness =
      data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
    if (brightness < 50) {
      data[i] = 0;
      data[i + 1] = 0;
      data[i + 2] = 0;
    }
  }

  ctx.putImageData(imageData, 0, 0);
  const newTexture = originalMap.clone();
  newTexture.image = canvas;
  newTexture.needsUpdate = true;
  return newTexture;
}

function HandModel({ pointer }) {
  const { scene } = useGLTF(MODEL_PATH);
  const groupRef = useRef();
  const [layout, setLayout] = useState({ offset: [0, 0, 0], scale: 1 });

  useEffect(() => {
    const upgradeMaterial = (material) => {
      const next = material.clone();

      if (material.map) {
        next.color.set("#020103");
        next.roughness = 0.95;
        next.metalness = 0.0;
        next.emissiveMap = createScreenOnlyEmissiveMap(material.map);
        next.emissive = new THREE.Color("#ffffff");
        next.emissiveIntensity = 1.2;
        next.needsUpdate = true;
      }
      return next;
    };

    scene.traverse((child) => {
      if (!child.isMesh || !child.material) return;

      child.material = Array.isArray(child.material)
        ? child.material.map(upgradeMaterial)
        : upgradeMaterial(child.material);
    });

    scene.updateMatrixWorld(true);

    const box = new THREE.Box3().setFromObject(scene);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const targetHeight = 2.5;

    setLayout({
      offset: [-center.x, -box.min.y, -center.z],
      scale: targetHeight / Math.max(size.y, 0.001),
    });
  }, [scene]);

  useFrame((state, delta) => {
    if (!groupRef.current) return;

    const px = pointer.current.x;
    const py = pointer.current.y;
    const restY = -1.18;
    const time = state.clock.elapsedTime;

    // If mouse/touch hasn't moved, do a gentle auto-rotate
    const isIdle = Math.abs(px) < 0.01 && Math.abs(py) < 0.01;

    const autoY = isIdle ? Math.sin(time * 0.8) * 0.25 : 0;
    const autoX = isIdle ? Math.sin(time * 0.5) * 0.08 : 0;
    const autoZ = isIdle ? Math.sin(time * 0.6) * 0.03 : 0;

    const lookY = Math.atan2(px * 7, 3);
    const lookX = Math.atan2(-py * 3, 4.5);
    const floatY = -1.3 + Math.sin(time * 1.1) * 0.02;

    easing.dampE(
      groupRef.current.rotation,
      [
        0.08 + lookX + autoX,
        restY + lookY + autoY,
        px * -0.05 + autoZ,
      ],
      isIdle ? 0.08 : 0.12,
      delta,
    );

    easing.damp3(
      groupRef.current.position,
      [px * 0.15, floatY, Math.abs(px) * -0.08],
      0.15,
      delta,
    );
  });

  return (
    <group
      ref={groupRef}
      scale={layout.scale}
      position={[0, -1.3, 0]}
      rotation={[0.08, -1.18, 0]}
    >
      <primitive object={scene} position={layout.offset} />
    </group>
  );
}

function HeroHand3D() {
  const pointer = useWindowPointer();

  return (
    <div className="hero-hand-3d">
      <Canvas
        camera={{ position: [0, 0.3, 3.7], fov: 40 }}
        style={{ width: "100%", height: "100%" }}
        gl={{ alpha: true, antialias: true }}
        dpr={[1, 2]}
      >
   <ambientLight intensity={0.03} />
<directionalLight position={[3.2, 3, 4.5]} intensity={0.25} color="#8b5cf6" />
<directionalLight position={[-3.5, 0.3, 4]} intensity={0.1} color="#4A8FFF" />
<directionalLight position={[0, 0.4, 5]} intensity={0.04} color="#ffffff" />
<directionalLight position={[0, 4, 2]} intensity={0.05} color="#7c3aed" />
<directionalLight position={[-2, 1, -3]} intensity={0.2} color="#8b5cf6" />
<directionalLight position={[2, -1, -3]} intensity={0.1} color="#6d3aed" />

        <Suspense fallback={null}>
          <HandModel pointer={pointer} />
        </Suspense>
      </Canvas>
    </div>
  );
}

useGLTF.preload(MODEL_PATH);

export default HeroHand3D;
export { HeroHand3D };